import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, ShieldCheck, Gauge, Banknote, ScrollText, FileCheck2, Users } from "lucide-react";
import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Home() {
  const navigate = useNavigate();
  return (
    <div data-testid="home-page">
      {/* Demo banner */}
      <div className="bg-amber-400 text-slate-900 text-center px-4 py-2.5 text-sm" data-testid="demo-banner">
        <span className="font-extrabold uppercase tracking-wide">LIVE</span>
        <span className="mx-2">—</span>
        NOT Processing Applications, Launching Soon
      </div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dotted-bg opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-teal-700 font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-600" />
              Red Fort Capital Finance NBFC
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 leading-[1.05] tracking-tight">
              Instant Used Car Loans<br />
              <span className="text-teal-600">Powered by Vehicle Intelligence.</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-slate-600 max-w-2xl leading-relaxed">
              India's first diagnostics-led used car financing platform. Apply, upload a CarDr diagnostic report, and receive a transparent, risk-adjusted loan offer from Red Fort Capital Finance NBFC.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-apply-btn" size="lg" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => navigate("/apply")}>
                Apply for Loan <ArrowRight className="ml-1" size={18} />
              </Button>
              <Button data-testid="hero-dealer-btn" size="lg" variant="outline" onClick={() => navigate("/dealer-partners")}>Dealer Partners</Button>
              <Button data-testid="hero-dsa-btn" size="lg" variant="outline" onClick={() => navigate("/dsa-partners")}>Become a DSA</Button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <div>
                <div className="text-2xl font-semibold text-slate-900 font-mono">5 min</div>
                <div className="text-xs text-slate-500 mt-1">Quick eligibility</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-slate-900 font-mono">80%</div>
                <div className="text-xs text-slate-500 mt-1">Max LTV (Grade A)</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-slate-900 font-mono">12 mo</div>
                <div className="text-xs text-slate-500 mt-1">Min tenure</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-teal-100 to-transparent rounded-3xl -z-10" />
              <img
                src="https://images.unsplash.com/photo-1650029378085-d997da8f4986?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Hyundai Creta — sample used vehicle"
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
                data-testid="hero-image"
              />
              <Card className="absolute -bottom-6 -left-6 p-4 w-64 shadow-lg border-slate-200">
                <div className="text-xs uppercase tracking-wider text-slate-500">Sample Decision</div>
                <div className="mt-1 font-semibold text-slate-900">Approved</div>
                <div className="text-sm text-slate-600 mt-2">₹8.5L · 60 months · 12.5% ROI</div>
                <div className="text-xs text-teal-700 mt-2 font-medium">CarDr Grade A · LTV 75%</div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* 3-step */}
      <section className="border-y border-slate-200 bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-xs uppercase tracking-wider text-teal-700 font-semibold mb-3">How it works</div>
          <h2 className="text-3xl lg:text-4xl font-semibold text-slate-900 mb-12 max-w-2xl">From application to disbursement — in three simple steps.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: ScrollText, title: "Apply", text: "Share borrower, employment, vehicle and loan details in a guided mobile-first journey." },
              { icon: FileCheck2, title: "Upload CarDr Report", text: "Upload the CarDr diagnostic PDF. We extract DTCs, health grade and repair exposure." },
              { icon: Banknote, title: "Get Loan Decision", text: "Red Fort Capital's underwriting engine returns valuation, LTV, EMI and sanction letter." },
            ].map((s, i) => (
              <Card key={i} className="p-6 border-slate-200" data-testid={`step-${i + 1}`}>
                <div className="w-10 h-10 rounded-lg bg-slate-900 text-white grid place-items-center mb-4">
                  <s.icon size={18} />
                </div>
                <div className="text-xs text-slate-500 font-mono">STEP {i + 1}</div>
                <div className="text-lg font-semibold text-slate-900 mt-1">{s.title}</div>
                <div className="text-sm text-slate-600 mt-2 leading-relaxed">{s.text}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 max-w-7xl mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {[
          { icon: Users, title: "For Borrowers", points: ["Transparent risk-adjusted offer", "5 minute eligibility", "EMI as low as ₹X / lakh", "Digital sanction & documents"] },
          { icon: ShieldCheck, title: "For Dealers", points: ["Faster approvals on inventory", "Higher conversion rate", "Dealer payout tracker", "Auto-flagged repair exposure"] },
          { icon: Gauge, title: "For DSAs", points: ["Lead-to-disbursal dashboard", "Commission visibility", "Pan-India reach", "Pre-qualification engine"] },
        ].map((b, i) => (
          <Card key={i} className="p-6 border-slate-200" data-testid={`benefit-${i}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-md bg-teal-50 text-teal-700 grid place-items-center"><b.icon size={18} /></div>
              <div className="font-semibold text-slate-900">{b.title}</div>
            </div>
            <ul className="text-sm text-slate-600 space-y-2">
              {b.points.map((p, j) => <li key={j} className="flex gap-2"><span className="text-teal-600">•</span><span>{p}</span></li>)}
            </ul>
          </Card>
        ))}
      </section>

      {/* CarDr advantage strip */}
      <section className="gradient-navy text-white py-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-wider text-teal-300 font-semibold mb-3">The CarDr advantage</div>
            <h2 className="text-3xl lg:text-4xl font-semibold leading-tight">Vehicle intelligence that protects every loan we underwrite.</h2>
            <p className="text-slate-300 mt-5 max-w-2xl leading-relaxed">CarDr's diagnostic platform reads the vehicle's onboard systems — engine codes, ABS, airbag, transmission, emissions — and gives us a deterministic view of repair exposure, code reset attempts, and odometer integrity before we lend.</p>
            <ul className="mt-6 grid grid-cols-2 gap-3 text-sm text-slate-200">
              {["DTC scan & health grade", "Repair exposure ₹ estimate", "Code reset & VIN integrity", "LTV adjusted by grade"].map((p, i) => (
                <li key={i} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-teal-400" />{p}</li>
              ))}
            </ul>
            <Button className="mt-8 bg-teal-500 hover:bg-teal-400 text-slate-900" data-testid="cardr-learn-btn" onClick={() => navigate("/cardr-advantage")}>Learn more</Button>
          </div>
          <div className="lg:col-span-5">
            <Card className="bg-slate-800 border-slate-700 text-white p-6">
              <div className="text-xs uppercase tracking-wider text-teal-300 mb-3">Sample CarDr summary</div>
              <div className="grid grid-cols-2 gap-y-3 text-sm">
                <div className="text-slate-400">Health Grade</div><div className="font-mono font-semibold text-teal-300">A</div>
                <div className="text-slate-400">DTC count</div><div className="font-mono">0 active</div>
                <div className="text-slate-400">Repair estimate</div><div className="font-mono">₹8,500</div>
                <div className="text-slate-400">Code reset</div><div className="font-mono">No</div>
                <div className="text-slate-400">VIN mismatch</div><div className="font-mono">No</div>
                <div className="text-slate-400">Recommended LTV</div><div className="font-mono text-teal-300">80%</div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* CTA */}
      <section className="bg-slate-50 py-20 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-3xl lg:text-4xl font-semibold text-slate-900">Ready to finance your next used car?</h2>
            <p className="text-slate-600 mt-3">Get a transparent offer in minutes. No paperwork until you accept.</p>
          </div>
          <div className="flex gap-3 lg:justify-end">
            <Button size="lg" className="bg-teal-600 hover:bg-teal-700" data-testid="footer-cta-apply" onClick={() => navigate("/apply")}>Apply for Loan</Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/contact")}>Talk to us</Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function FAQ() {
  return (
    <section className="py-20 max-w-4xl mx-auto px-4 lg:px-8">
      <div className="text-xs uppercase tracking-wider text-teal-700 font-semibold mb-3">FAQ</div>
      <h2 className="text-3xl font-semibold text-slate-900 mb-8">Frequently asked questions</h2>
      <Accordion type="single" collapsible className="space-y-2">
        {[
          { q: "Who is Red Fort Capital Finance?", a: "A Reserve Bank of India registered NBFC operating the CarFin India platform — the lender of record, credit policy owner, and disbursement authority for every loan." },
          { q: "Why do you need a CarDr diagnostic report?", a: "CarDr's diagnostic scan tells us the true repair exposure, code reset history and odometer integrity of the vehicle. We adjust LTV and pricing based on the diagnostic grade." },
          { q: "How fast is the loan decision?", a: "Quick eligibility takes ~5 minutes. Full underwriting decision is returned within working hours once documents and CarDr report are uploaded." },
          { q: "What is the maximum LTV?", a: "Up to 80% for CarDr Grade A vehicles. Grade B, C, D vehicles are financed at lower LTVs as per Red Fort Capital credit policy." },
          { q: "Can dealers and DSAs onboard borrowers?", a: "Yes. Dealer and DSA portals allow lead creation, document upload, and end-to-end application tracking with commission visibility." },
        ].map((f, i) => (
          <AccordionItem value={`q${i}`} key={i} className="border border-slate-200 rounded-lg px-4" data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
            <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}
